import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import { 
  getUnits, 
  getProjects, 
  getTowers, 
  changeUnitStatus, 
  createUnit, 
  createUnitsBulk,
  uploadUnitsCSV,
  downloadCSVTemplate,
  getChannels,
  createTower,
  updateTower
} from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Home, 
  Grid3X3, 
  List, 
  Building, 
  Plus, 
  Search,
  Loader2,
  Lock,
  CheckCircle2,
  Clock,
  X,
  Upload,
  Download,
  FileSpreadsheet,
  Building2,
  MoreVertical,
  AlertCircle,
  Settings2,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
// Sub-components
import { getStatusBadge as _getStatusBadge, getStatusIcon as _getStatusIcon, formatPrice as _formatPrice, canChangeStatus as _canChangeStatus, DEFAULT_COLUMNS as _DEFAULT_COLUMNS } from './inventory/inventoryHelpers';
import { UnitPeopleTab } from './inventory/UnitPeopleTab';
import { StatusChangeDialog } from './inventory/StatusChangeDialog';
import { OccupancyDialog } from './inventory/OccupancyDialog';
import { BulkActionBar } from './inventory/BulkActionBar';
import { BulkStatusDialog } from './inventory/BulkStatusDialog';
import { BulkOccupancyDialog } from './inventory/BulkOccupancyDialog';


// Default columns configuration
const DEFAULT_COLUMNS = [
  { id: 'project', label: 'Project', visible: true, required: true },
  { id: 'unit', label: 'Unit', visible: true, required: true },
  { id: 'tower', label: 'Tower', visible: true, required: false },
  { id: 'type', label: 'Type', visible: true, required: false },
  { id: 'configuration', label: 'Configuration', visible: true, required: false },
  { id: 'floor', label: 'Floor', visible: true, required: false },
  { id: 'area', label: 'Area', visible: true, required: false },
  { id: 'price', label: 'Price', visible: true, required: false },
  { id: 'status', label: 'Status', visible: true, required: true },
  { id: 'block_expiry', label: 'Block Expiry', visible: true, required: false },
];

const InventoryPage = () => {
  const { user } = useAuth();
  const { isReadOnly } = usePlan();
  const [searchParams, setSearchParams] = useSearchParams();
  const [units, setUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [towers, setTowers] = useState([]);
  const [allTowers, setAllTowers] = useState([]); // All towers across projects
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // grid, list, tower
  
  // Column customization
  const [columns, setColumns] = useState(() => {
    const saved = localStorage.getItem('inventoryColumns');
    return saved ? JSON.parse(saved) : DEFAULT_COLUMNS;
  });
  
  // Filters
  const [selectedProject, setSelectedProject] = useState(searchParams.get('project') || 'all');
  const [selectedTower, setSelectedTower] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedOccupancy, setSelectedOccupancy] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [isUnitDialogOpen, setIsUnitDialogOpen] = useState(false);
  const [isAddUnitDialogOpen, setIsAddUnitDialogOpen] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isAddTowerDialogOpen, setIsAddTowerDialogOpen] = useState(false);
  const [isEditTowerDialogOpen, setIsEditTowerDialogOpen] = useState(false);
  const [editingTower, setEditingTower] = useState(null);
  const [statusAction, setStatusAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // File upload
  const fileInputRef = useRef(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  
  // Status change form
  const [isOccupancyDialogOpen, setIsOccupancyDialogOpen] = useState(false);
  const [occupancyAction, setOccupancyAction] = useState('');

  // Bulk selection
  const [selectedUnitIds, setSelectedUnitIds] = useState([]);
  const [isBulkStatusDialogOpen, setIsBulkStatusDialogOpen] = useState(false);
  const [isBulkOccupancyDialogOpen, setIsBulkOccupancyDialogOpen] = useState(false);
  const [bulkStatusAction, setBulkStatusAction] = useState(null);
  
  // Add unit form
  const [unitForm, setUnitForm] = useState({
    unit_number: '',
    project_id: '',
    tower_id: '',
    unit_type: 'apartment',
    configuration: '',
    area_sqft: '',
    base_price: '',
    floor_number: ''
  });

  // Add tower form
  const [towerForm, setTowerForm] = useState({
    name: '',
    code: '',
    project_id: '',
    total_floors: '',
    units_per_floor: ''
  });

  // Save columns to localStorage
  useEffect(() => {
    localStorage.setItem('inventoryColumns', JSON.stringify(columns));
  }, [columns]);

  const toggleColumn = (columnId) => {
    setColumns(cols => cols.map(col => 
      col.id === columnId && !col.required 
        ? { ...col, visible: !col.visible }
        : col
    ));
  };

  const loadData = useCallback(async () => {
    try {
      const [projectsData, channelsData, allTowersData] = await Promise.all([
        getProjects(),
        getChannels(),
        getTowers()
      ]);
      setProjects(projectsData);
      setChannels(channelsData);
      setAllTowers(allTowersData);
      
      if (selectedProject && selectedProject !== 'all') {
        const towersData = await getTowers(selectedProject);
        setTowers(towersData);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  }, [selectedProject]);

  const loadUnits = useCallback(async () => {
    setLoading(true);
    setSelectedUnitIds([]);
    try {
      const filters = {};
      if (selectedProject && selectedProject !== 'all') filters.project_id = selectedProject;
      if (selectedTower && selectedTower !== 'all') filters.tower_id = selectedTower;
      if (selectedStatus) filters.status = selectedStatus;
      if (selectedOccupancy && selectedOccupancy !== 'all_occ') filters.occupancy_status = selectedOccupancy;
      
      const data = await getUnits(filters);
      setUnits(data);
    } catch (error) {
      console.error('Failed to load units:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [selectedProject, selectedTower, selectedStatus, selectedOccupancy]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  useEffect(() => {
    if (selectedProject && selectedProject !== 'all') {
      getTowers(selectedProject).then(setTowers).catch(console.error);
      setSearchParams({ project: selectedProject });
    } else {
      setTowers([]);
      setSearchParams({});
    }
    setSelectedTower('all');
  }, [selectedProject, setSearchParams]);

  // Helper functions
  const getProjectName = (projectId) => {
    const project = projects.find(p => p.project_id === projectId);
    return project?.name || projectId;
  };

  const getTowerName = (towerId) => {
    const tower = allTowers.find(t => t.tower_id === towerId);
    return tower?.name || '-';
  };

  const getProjectTowerName = (projectId, towerId) => {
    const project = projects.find(p => p.project_id === projectId);
    const tower = allTowers.find(t => t.tower_id === towerId);
    if (project && tower) {
      return `${project.name} - ${tower.name}`;
    } else if (project) {
      return project.name;
    }
    return 'Unassigned';
  };

  const handleStatusChange = async (unit, newStatus) => {
    setSelectedUnit(unit);
    setStatusAction(newStatus);
    setIsStatusDialogOpen(true);
  };

  const handleOccupancyChange = (unit, action) => {
    setSelectedUnit(unit);
    setOccupancyAction(action);
    setIsOccupancyDialogOpen(true);
  };

  const endOccupancy = async (unitId, type) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    try {
      if (type === 'rented') {
        const rentals = await fetch(`${API_URL}/api/v1/occupancy/units/${unitId}/rentals`, { credentials: 'include' }).then(r => r.json());
        const active = rentals.find(r => r.status === 'active');
        if (active) {
          await fetch(`${API_URL}/api/v1/occupancy/units/${unitId}/rentals/${active.rental_id}/end`, { method: 'POST', credentials: 'include' });
        }
      } else {
        const history = await fetch(`${API_URL}/api/v1/occupancy/units/${unitId}/occupancy-history`, { credentials: 'include' }).then(r => r.json());
        const active = history.find(o => o.status === 'active' && o.occupancy_type === 'owner_occupied');
        if (active) {
          await fetch(`${API_URL}/api/v1/occupancy/units/${unitId}/owner-occupancy/${active.occupancy_id}/end`, { method: 'POST', credentials: 'include' });
        }
      }
      toast.success('Occupancy ended');
      setIsUnitDialogOpen(false);
      loadUnits();
    } catch (error) {
      toast.error('Failed to end occupancy');
    }
  };

  const handleAddUnit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const unitData = {
        ...unitForm,
        area_sqft: parseFloat(unitForm.area_sqft),
        base_price: parseFloat(unitForm.base_price),
        floor_number: unitForm.floor_number ? parseInt(unitForm.floor_number) : null,
        tower_id: unitForm.tower_id || null
      };
      
      await createUnit(unitData);
      toast.success('Unit created successfully');
      setIsAddUnitDialogOpen(false);
      setUnitForm({
        unit_number: '',
        project_id: '',
        tower_id: '',
        unit_type: 'apartment',
        configuration: '',
        area_sqft: '',
        base_price: '',
        floor_number: ''
      });
      loadUnits();
    } catch (error) {
      console.error('Failed to create unit:', error);
      toast.error(error.message || 'Failed to create unit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddTower = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await createTower({
        ...towerForm,
        total_floors: parseInt(towerForm.total_floors) || 0,
        units_per_floor: parseInt(towerForm.units_per_floor) || 0
      });
      toast.success('Tower created successfully');
      setIsAddTowerDialogOpen(false);
      setTowerForm({ name: '', code: '', project_id: '', total_floors: '', units_per_floor: '' });
      loadData();
    } catch (error) {
      console.error('Failed to create tower:', error);
      toast.error(error.message || 'Failed to create tower');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTower = async (e) => {
    e.preventDefault();
    if (!editingTower) return;
    
    setSubmitting(true);
    try {
      await updateTower(editingTower.tower_id, {
        name: towerForm.name,
        code: towerForm.code,
        project_id: towerForm.project_id,
        total_floors: parseInt(towerForm.total_floors) || 0,
        units_per_floor: parseInt(towerForm.units_per_floor) || 0
      });
      toast.success('Tower updated successfully');
      setIsEditTowerDialogOpen(false);
      setEditingTower(null);
      setTowerForm({ name: '', code: '', project_id: '', total_floors: '', units_per_floor: '' });
      loadData();
    } catch (error) {
      console.error('Failed to update tower:', error);
      toast.error(error.message || 'Failed to update tower');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditTowerDialog = (tower) => {
    setEditingTower(tower);
    setTowerForm({
      name: tower.name,
      code: tower.code,
      project_id: tower.project_id,
      total_floors: tower.total_floors?.toString() || '',
      units_per_floor: tower.units_per_floor?.toString() || ''
    });
    setIsEditTowerDialogOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      setUploadResult(null);
    }
  };

  const handleUploadCSV = async () => {
    if (!uploadFile) return;
    
    setSubmitting(true);
    try {
      const projectId = selectedProject !== 'all' ? selectedProject : null;
      const result = await uploadUnitsCSV(uploadFile, projectId);
      setUploadResult(result);
      
      if (result.created_count > 0) {
        toast.success(`Successfully created ${result.created_count} units`);
        loadUnits();
      }
      
      if (result.error_count > 0) {
        toast.warning(`${result.error_count} rows had errors`);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.message || 'Failed to upload CSV');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredUnits = units.filter(unit => 
    unit.unit_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    unit.configuration.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Bulk selection helpers
  const toggleUnitSelect = (unitId) => {
    setSelectedUnitIds(prev =>
      prev.includes(unitId) ? prev.filter(id => id !== unitId) : [...prev, unitId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUnitIds.length === filteredUnits.length) {
      setSelectedUnitIds([]);
    } else {
      setSelectedUnitIds(filteredUnits.map(u => u.unit_id));
    }
  };

  const handleBulkStatusAction = (action) => {
    setBulkStatusAction(action);
    setIsBulkStatusDialogOpen(true);
  };

  const handleBulkOccupancyAction = () => {
    setIsBulkOccupancyDialogOpen(true);
  };

  const handleBulkSuccess = () => {
    setSelectedUnitIds([]);
    loadUnits();
  };

  const getStatusBadge = _getStatusBadge;
  const getStatusIcon = _getStatusIcon;
  const formatPrice = _formatPrice;
  const canChangeStatus = (unit, newStatus) => _canChangeStatus(unit, newStatus, user?.role);

  const statusCounts = {
    all: units.length,
    available: units.filter(u => u.status === 'available').length,
    soft_blocked: units.filter(u => u.status === 'soft_blocked').length,
    booked: units.filter(u => u.status === 'booked').length
  };

  // Tower View - organize units by floor and tower with project names
  const getTowerViewData = () => {
    const towerMap = {};
    
    filteredUnits.forEach(unit => {
      const towerId = unit.tower_id || 'no_tower';
      const floor = unit.floor_number || 0;
      
      if (!towerMap[towerId]) {
        const tower = allTowers.find(t => t.tower_id === towerId);
        const project = projects.find(p => p.project_id === unit.project_id);
        
        towerMap[towerId] = {
          tower: tower || { name: 'Unassigned', code: 'NA', tower_id: 'no_tower' },
          project: project || { name: 'Unknown', project_id: unit.project_id },
          displayName: tower && project 
            ? `${project.name} - ${tower.name}` 
            : project 
            ? project.name 
            : 'Unassigned',
          floors: {}
        };
      }
      
      if (!towerMap[towerId].floors[floor]) {
        towerMap[towerId].floors[floor] = [];
      }
      
      towerMap[towerId].floors[floor].push(unit);
    });
    
    // Sort floors in descending order (top floor first)
    Object.keys(towerMap).forEach(towerId => {
      const sortedFloors = {};
      Object.keys(towerMap[towerId].floors)
        .sort((a, b) => parseInt(b) - parseInt(a))
        .forEach(floor => {
          sortedFloors[floor] = towerMap[towerId].floors[floor];
        });
      towerMap[towerId].floors = sortedFloors;
    });
    
    return towerMap;
  };

  const towerViewData = getTowerViewData();

  // Get visible columns
  const visibleColumns = columns.filter(col => col.visible);

  return (
    <div className="space-y-6 animate-in" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Inventory</h1>
          <p className="text-muted-foreground mt-1">
            View and manage unit availability across projects
          </p>
        </div>
        
        {(user?.role === 'admin' || user?.role === 'management') && !isReadOnly && (
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="add-menu-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setIsAddUnitDialogOpen(true)}>
                  <Home className="w-4 h-4 mr-2" />
                  Add Unit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  setTowerForm({ ...towerForm, project_id: selectedProject !== 'all' ? selectedProject : '' });
                  setIsAddTowerDialogOpen(true);
                }}>
                  <Building className="w-4 h-4 mr-2" />
                  Add Tower
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setIsUploadDialogOpen(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Bulk Upload CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={downloadCSVTemplate}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger data-testid="project-filter">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {towers.length > 0 && (
              <div className="flex-1 min-w-[200px]">
                <Select value={selectedTower} onValueChange={setSelectedTower}>
                  <SelectTrigger data-testid="tower-filter">
                    <SelectValue placeholder="All Towers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Towers</SelectItem>
                    {towers.map(t => (
                      <SelectItem key={t.tower_id} value={t.tower_id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search units..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-units"
              />
            </div>
            
            <div className="flex items-center gap-1 border rounded-md p-1">
              <Button 
                variant={viewMode === 'grid' ? 'default' : 'ghost'} 
                size="icon"
                className="h-8 w-8"
                onClick={() => setViewMode('grid')}
                data-testid="view-grid"
                title="Grid View"
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
              <Button 
                variant={viewMode === 'list' ? 'default' : 'ghost'} 
                size="icon"
                className="h-8 w-8"
                onClick={() => setViewMode('list')}
                data-testid="view-list"
                title="List View"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button 
                variant={viewMode === 'tower' ? 'default' : 'ghost'} 
                size="icon"
                className="h-8 w-8"
                onClick={() => setViewMode('tower')}
                data-testid="view-tower"
                title="Tower View"
              >
                <Building className="w-4 h-4" />
              </Button>
              
              {/* Column customization for list view */}
              {viewMode === 'list' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Customize Columns">
                      <Settings2 className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel>Show Columns</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {columns.map(col => (
                      <DropdownMenuCheckboxItem
                        key={col.id}
                        checked={col.visible}
                        onCheckedChange={() => toggleColumn(col.id)}
                        disabled={col.required}
                      >
                        {col.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Tabs + Occupancy Filter */}
      <div className="flex items-center gap-4 flex-wrap">
        <Tabs value={selectedStatus} onValueChange={setSelectedStatus}>
          <TabsList>
            <TabsTrigger value="" data-testid="tab-all">
              All ({statusCounts.all})
            </TabsTrigger>
            <TabsTrigger value="available" data-testid="tab-available">
              <CheckCircle2 className="w-4 h-4 mr-1 text-emerald-600" />
              Available ({statusCounts.available})
            </TabsTrigger>
            <TabsTrigger value="soft_blocked" data-testid="tab-blocked">
              <Clock className="w-4 h-4 mr-1 text-amber-600" />
              Blocked ({statusCounts.soft_blocked})
            </TabsTrigger>
            <TabsTrigger value="booked" data-testid="tab-booked">
              <Lock className="w-4 h-4 mr-1 text-rose-600" />
              Booked ({statusCounts.booked})
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={selectedOccupancy} onValueChange={setSelectedOccupancy}>
          <SelectTrigger className="w-[160px] h-9 text-xs" data-testid="occupancy-filter">
            <SelectValue placeholder="All Occupancy" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all_occ">All Occupancy</SelectItem>
            <SelectItem value="vacant">Vacant</SelectItem>
            <SelectItem value="rented">Rented</SelectItem>
            <SelectItem value="owner_occupied">Owner Occupied</SelectItem>
          </SelectContent>
        </Select>
        {selectedOccupancy && selectedOccupancy !== 'all_occ' && (
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelectedOccupancy('')}>
            Clear occupancy filter
          </Button>
        )}
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredUnits.length === 0 ? (
        <Card className="p-12 text-center">
          <Home className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No units found</h3>
          <p className="text-muted-foreground mb-4">
            {selectedProject !== 'all' ? 'No units match your filters' : 'Select a project or add units to get started'}
          </p>
          {!isReadOnly && (user?.role === 'admin' || user?.role === 'management') && (
            <div className="flex items-center justify-center gap-2">
              <Button onClick={() => setIsAddUnitDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Unit
              </Button>
              <Button variant="outline" onClick={() => setIsUploadDialogOpen(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Bulk Upload
              </Button>
            </div>
          )}
        </Card>
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
          {filteredUnits.map(unit => (
            <div
              key={unit.unit_id}
              className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md relative ${
                selectedUnitIds.includes(unit.unit_id) ? 'ring-2 ring-primary ring-offset-1 ' : ''
              }${
                unit.status === 'available' 
                  ? 'border-emerald-200 bg-emerald-50 hover:border-emerald-400 dark:border-emerald-800 dark:bg-emerald-950/30' 
                  : unit.status === 'soft_blocked'
                  ? 'border-amber-200 bg-amber-50 hover:border-amber-400 dark:border-amber-800 dark:bg-amber-950/30'
                  : 'border-rose-200 bg-rose-50 hover:border-rose-400 dark:border-rose-800 dark:bg-rose-950/30'
              }`}
              onClick={() => { setSelectedUnit(unit); setIsUnitDialogOpen(true); }}
              data-testid={`unit-cell-${unit.unit_id}`}
            >
              {!isReadOnly && (
                <div className="absolute top-1 right-1 z-10" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedUnitIds.includes(unit.unit_id)}
                    onCheckedChange={() => toggleUnitSelect(unit.unit_id)}
                    className="h-4 w-4"
                    data-testid={`select-unit-${unit.unit_id}`}
                  />
                </div>
              )}
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-sm font-semibold">{unit.unit_number}</span>
                {getStatusIcon(unit.status)}
              </div>
              <p className="text-xs text-muted-foreground">{unit.configuration}</p>
              <p className="text-xs font-medium mt-1">{formatPrice(unit.base_price)}</p>
            </div>
          ))}
        </div>
      ) : viewMode === 'list' ? (
        /* Table View with Customizable Columns */
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                {!isReadOnly && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={selectedUnitIds.length === filteredUnits.length && filteredUnits.length > 0}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-units"
                    />
                  </TableHead>
                )}
                {visibleColumns.map(col => (
                  <TableHead key={col.id}>{col.label}</TableHead>
                ))}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUnits.map(unit => (
                <TableRow 
                  key={unit.unit_id} 
                  className={`cursor-pointer ${selectedUnitIds.includes(unit.unit_id) ? 'bg-primary/5' : ''}`}
                  onClick={() => { setSelectedUnit(unit); setIsUnitDialogOpen(true); }}
                  data-testid={`unit-row-${unit.unit_id}`}
                >
                  {!isReadOnly && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedUnitIds.includes(unit.unit_id)}
                        onCheckedChange={() => toggleUnitSelect(unit.unit_id)}
                        data-testid={`select-unit-${unit.unit_id}`}
                      />
                    </TableCell>
                  )}
                  {visibleColumns.map(col => (
                    <TableCell key={col.id}>
                      {col.id === 'project' && getProjectName(unit.project_id)}
                      {col.id === 'unit' && <span className="font-mono font-medium">{unit.unit_number}</span>}
                      {col.id === 'tower' && getTowerName(unit.tower_id)}
                      {col.id === 'type' && <span className="capitalize">{unit.unit_type}</span>}
                      {col.id === 'configuration' && unit.configuration}
                      {col.id === 'floor' && (unit.floor_number || '-')}
                      {col.id === 'area' && `${unit.area_sqft.toLocaleString()} sq ft`}
                      {col.id === 'price' && formatPrice(unit.base_price)}
                      {col.id === 'status' && getStatusBadge(unit.status)}
                      {col.id === 'block_expiry' && (
                        unit.status === 'soft_blocked' && unit.block_expiry ? (
                          <span className="text-amber-600 text-sm">
                            {formatDistanceToNow(new Date(unit.block_expiry), { addSuffix: true })}
                          </span>
                        ) : '-'
                      )}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setSelectedUnit(unit); setIsUnitDialogOpen(true); }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        /* Tower View - Visual Building Representation with Project Names */
        <div className="space-y-6" data-testid="tower-view">
          {Object.keys(towerViewData).length === 0 ? (
            <Card className="p-12 text-center">
              <Building className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No tower data available</h3>
              <p className="text-muted-foreground">
                Add towers and assign units with floor numbers to see the tower view
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {Object.entries(towerViewData).map(([towerId, towerData]) => (
                <Card key={towerId} className="overflow-hidden" data-testid={`tower-card-${towerId}`}>
                  <CardHeader className="bg-muted/50 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-1">
                        <Building2 className="w-5 h-5 text-primary flex-shrink-0" />
                        <div className="min-w-0">
                          <CardTitle className="text-base truncate" title={towerData.displayName}>
                            {towerData.displayName}
                          </CardTitle>
                          <p className="text-xs text-muted-foreground">
                            {towerData.tower.code !== 'NA' && `Code: ${towerData.tower.code}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-sm bg-emerald-500"></span>
                            {Object.values(towerData.floors).flat().filter(u => u.status === 'available').length}
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-sm bg-amber-500"></span>
                            {Object.values(towerData.floors).flat().filter(u => u.status === 'soft_blocked').length}
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-sm bg-rose-500"></span>
                            {Object.values(towerData.floors).flat().filter(u => u.status === 'booked').length}
                          </span>
                        </div>
                        {(user?.role === 'admin' || user?.role === 'management') && towerData.tower.tower_id !== 'no_tower' && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditTowerDialog(towerData.tower);
                            }}
                            title="Edit Tower"
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[400px]">
                      <div className="p-4 space-y-2">
                        {Object.entries(towerData.floors).map(([floor, floorUnits]) => (
                          <div key={floor} className="flex items-stretch gap-2">
                            {/* Floor label */}
                            <div className="w-12 flex-shrink-0 flex items-center justify-center bg-muted rounded-l-md font-mono text-sm font-medium">
                              F{floor}
                            </div>
                            {/* Units on this floor */}
                            <div className="flex-1 flex flex-wrap gap-1 p-2 bg-muted/30 rounded-r-md border-l-4 border-muted">
                              {floorUnits.map(unit => (
                                <button
                                  key={unit.unit_id}
                                  onClick={() => { setSelectedUnit(unit); setIsUnitDialogOpen(true); }}
                                  className={`px-2 py-1 rounded text-xs font-mono font-medium transition-all hover:scale-105 hover:shadow-md ${
                                    unit.status === 'available' 
                                      ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                                      : unit.status === 'soft_blocked'
                                      ? 'bg-amber-500 text-white hover:bg-amber-600'
                                      : 'bg-rose-500 text-white hover:bg-rose-600'
                                  }`}
                                  title={`${unit.unit_number} - ${unit.configuration} - ${formatPrice(unit.base_price)}`}
                                  data-testid={`tower-unit-${unit.unit_id}`}
                                >
                                  {unit.unit_number.split('-').pop() || unit.unit_number}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {/* Legend */}
          <Card className="p-4">
            <div className="flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded bg-emerald-500"></span>
                <span>Available</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded bg-amber-500"></span>
                <span>Soft Blocked</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded bg-rose-500"></span>
                <span>Booked</span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Unit Detail Dialog */}
      <Dialog open={isUnitDialogOpen} onOpenChange={setIsUnitDialogOpen}>
        <DialogContent className="sm:max-w-[580px] max-h-[85vh] flex flex-col">
          {selectedUnit && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Home className="w-5 h-5" />
                  Unit {selectedUnit.unit_number}
                </DialogTitle>
                <DialogDescription>
                  {getProjectName(selectedUnit.project_id)} • {selectedUnit.configuration} • {selectedUnit.area_sqft.toLocaleString()} sq ft
                </DialogDescription>
              </DialogHeader>
              
              <Tabs defaultValue="details" className="flex-1 flex flex-col min-h-0">
                <TabsList className="grid w-full grid-cols-2 mb-3">
                  <TabsTrigger value="details" data-testid="unit-tab-details">Details</TabsTrigger>
                  <TabsTrigger value="people" data-testid="unit-tab-people">People & Transactions</TabsTrigger>
                </TabsList>

                {/* Details Tab */}
                <TabsContent value="details" className="space-y-4 overflow-y-auto flex-1">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                    <div>
                      <p className="text-sm text-muted-foreground">Sales Status</p>
                      <div className="mt-1">{getStatusBadge(selectedUnit.status)}</div>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Occupancy</p>
                      <Badge variant="outline" className="mt-1 text-xs capitalize">{(selectedUnit.occupancy_status || 'vacant').replace('_', ' ')}</Badge>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Base Price</p>
                      <p className="text-lg font-bold">{formatPrice(selectedUnit.base_price)}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Type</p>
                      <p className="font-medium capitalize">{selectedUnit.unit_type}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Floor</p>
                      <p className="font-medium">{selectedUnit.floor_number || 'N/A'}</p>
                    </div>
                    {selectedUnit.tower_id && (
                      <div className="col-span-2">
                        <p className="text-sm text-muted-foreground">Tower</p>
                        <p className="font-medium">{getTowerName(selectedUnit.tower_id)}</p>
                      </div>
                    )}
                  </div>
                  
                  {selectedUnit.status === 'soft_blocked' && selectedUnit.block_expiry && (
                    <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800">
                      <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Block expires {formatDistanceToNow(new Date(selectedUnit.block_expiry), { addSuffix: true })}
                        </span>
                      </div>
                      {selectedUnit.status_reason && (
                        <p className="text-sm text-amber-600 mt-1">Reason: {selectedUnit.status_reason}</p>
                      )}
                    </div>
                  )}
                  
                  {selectedUnit.status === 'booked' && selectedUnit.buyer_reference && (
                    <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-800">
                      <div className="flex items-center gap-2 text-rose-700 dark:text-rose-400">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="text-sm font-medium">Booking Reference: {selectedUnit.buyer_reference}</span>
                      </div>
                    </div>
                  )}

                  {selectedUnit.status === 'sold' && selectedUnit.sale_reference && (
                    <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="text-sm font-medium">Sale Reference: {selectedUnit.sale_reference}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2 pt-4 border-t">
                    {isReadOnly && (
                      <p className="text-xs text-muted-foreground w-full text-center py-2">Account is in read-only mode. Contact Nine Degree to restore access.</p>
                    )}
                    {!isReadOnly && selectedUnit.status === 'available' && (
                      <>
                        {canChangeStatus(selectedUnit, 'soft_blocked') && (
                          <Button variant="outline" className="flex-1" onClick={() => handleStatusChange(selectedUnit, 'soft_blocked')} data-testid="block-unit-btn">
                            <Clock className="w-4 h-4 mr-2" /> Soft Block
                          </Button>
                        )}
                        {canChangeStatus(selectedUnit, 'booked') && (
                          <Button className="flex-1" onClick={() => handleStatusChange(selectedUnit, 'booked')} data-testid="book-unit-btn">
                            <CheckCircle2 className="w-4 h-4 mr-2" /> Book
                          </Button>
                        )}
                      </>
                    )}
                    {!isReadOnly && selectedUnit.status === 'soft_blocked' && (
                      <>
                        <Button variant="outline" className="flex-1" onClick={() => handleStatusChange(selectedUnit, 'available')} data-testid="release-unit-btn">
                          <X className="w-4 h-4 mr-2" /> Release
                        </Button>
                        {canChangeStatus(selectedUnit, 'booked') && (
                          <Button className="flex-1" onClick={() => handleStatusChange(selectedUnit, 'booked')} data-testid="convert-book-btn">
                            <CheckCircle2 className="w-4 h-4 mr-2" /> Convert to Booking
                          </Button>
                        )}
                      </>
                    )}
                    {!isReadOnly && selectedUnit.status === 'booked' && (
                      <>
                        {canChangeStatus(selectedUnit, 'available') && (
                          <Button variant="outline" className="flex-1" onClick={() => handleStatusChange(selectedUnit, 'available')} data-testid="cancel-booking-btn">
                            <X className="w-4 h-4 mr-2" /> Cancel Booking
                          </Button>
                        )}
                        {user?.role && ['admin', 'management'].includes(user.role) && (
                          <Button className="flex-1" onClick={() => handleStatusChange(selectedUnit, 'sold')} data-testid="mark-sold-btn">
                            <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Sold
                          </Button>
                        )}
                      </>
                    )}
                    {!isReadOnly && selectedUnit.status === 'sold' && user?.role && ['admin', 'management'].includes(user.role) && (
                      <Button variant="outline" className="flex-1" onClick={() => handleStatusChange(selectedUnit, 'on_hold')} data-testid="hold-unit-btn">
                        <AlertCircle className="w-4 h-4 mr-2" /> Put On Hold
                      </Button>
                    )}
                    {!isReadOnly && selectedUnit.status === 'on_hold' && user?.role && ['admin', 'management'].includes(user.role) && (
                      <Button variant="outline" className="flex-1" onClick={() => handleStatusChange(selectedUnit, 'available')} data-testid="release-hold-btn">
                        <X className="w-4 h-4 mr-2" /> Release Hold
                      </Button>
                    )}
                  </div>

                  {/* Occupancy Actions */}
                  {!isReadOnly && (
                    <div className="pt-3 border-t space-y-2">
                      <p className="text-xs font-semibold text-muted-foreground">Occupancy: <span className="capitalize text-foreground">{(selectedUnit.occupancy_status || 'vacant').replace('_', ' ')}</span></p>
                      {selectedUnit.occupancy_status === 'vacant' && (
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => handleOccupancyChange(selectedUnit, 'rented')} data-testid="set-rented-btn">
                            Set as Rented
                          </Button>
                          <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => handleOccupancyChange(selectedUnit, 'owner_occupied')} data-testid="set-owner-occupied-btn">
                            Owner Occupied
                          </Button>
                        </div>
                      )}
                      {selectedUnit.occupancy_status === 'rented' && (
                        <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => endOccupancy(selectedUnit.unit_id, 'rented')} data-testid="end-rental-btn">
                          End Rental
                        </Button>
                      )}
                      {selectedUnit.occupancy_status === 'owner_occupied' && (
                        <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => endOccupancy(selectedUnit.unit_id, 'owner_occupied')} data-testid="end-owner-occ-btn">
                          End Owner Occupancy
                        </Button>
                      )}
                    </div>
                  )}
                </TabsContent>

                {/* People & Transactions Tab */}
                <TabsContent value="people" className="flex-1 overflow-y-auto" data-testid="unit-people-tab">
                  <UnitPeopleTab unitId={selectedUnit.unit_id} />
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog — Sub-component */}
      <StatusChangeDialog
        open={isStatusDialogOpen}
        onOpenChange={setIsStatusDialogOpen}
        selectedUnit={selectedUnit}
        statusAction={statusAction}
        onSuccess={(action) => {
          toast.success(`Unit ${action === 'available' ? 'released' : action.replace('_', ' ')}`);
          setIsStatusDialogOpen(false);
          setIsUnitDialogOpen(false);
          loadUnits();
        }}
        submitting={submitting}
        setSubmitting={setSubmitting}
      />

      {/* Add Unit Dialog */}
      <Dialog open={isAddUnitDialogOpen} onOpenChange={setIsAddUnitDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New Unit</DialogTitle>
            <DialogDescription>Create a new inventory unit</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddUnit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Unit Number</Label>
                <Input
                  value={unitForm.unit_number}
                  onChange={(e) => setUnitForm({ ...unitForm, unit_number: e.target.value })}
                  placeholder="A-101"
                  required
                  data-testid="new-unit-number"
                />
              </div>
              <div className="space-y-2">
                <Label>Floor</Label>
                <Input
                  type="number"
                  value={unitForm.floor_number}
                  onChange={(e) => setUnitForm({ ...unitForm, floor_number: e.target.value })}
                  placeholder="1"
                  data-testid="new-unit-floor"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Project</Label>
              <Select 
                value={unitForm.project_id} 
                onValueChange={(v) => setUnitForm({ ...unitForm, project_id: v })}
                required
              >
                <SelectTrigger data-testid="new-unit-project">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {towers.length > 0 && (
              <div className="space-y-2">
                <Label>Tower (Optional)</Label>
                <Select 
                  value={unitForm.tower_id} 
                  onValueChange={(v) => setUnitForm({ ...unitForm, tower_id: v })}
                >
                  <SelectTrigger data-testid="new-unit-tower">
                    <SelectValue placeholder="Select tower" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No Tower</SelectItem>
                    {towers.map(t => (
                      <SelectItem key={t.tower_id} value={t.tower_id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Unit Type</Label>
                <Select 
                  value={unitForm.unit_type} 
                  onValueChange={(v) => setUnitForm({ ...unitForm, unit_type: v })}
                >
                  <SelectTrigger data-testid="new-unit-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                    <SelectItem value="plot">Plot</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Configuration</Label>
                <Input
                  value={unitForm.configuration}
                  onChange={(e) => setUnitForm({ ...unitForm, configuration: e.target.value })}
                  placeholder="2BHK, 3BHK, 500 sq yd"
                  required
                  data-testid="new-unit-config"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Area (sq ft)</Label>
                <Input
                  type="number"
                  value={unitForm.area_sqft}
                  onChange={(e) => setUnitForm({ ...unitForm, area_sqft: e.target.value })}
                  placeholder="1200"
                  required
                  data-testid="new-unit-area"
                />
              </div>
              <div className="space-y-2">
                <Label>Base Price (₹)</Label>
                <Input
                  type="number"
                  value={unitForm.base_price}
                  onChange={(e) => setUnitForm({ ...unitForm, base_price: e.target.value })}
                  placeholder="5000000"
                  required
                  data-testid="new-unit-price"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAddUnitDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-unit-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create Unit
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Tower Dialog */}
      <Dialog open={isAddTowerDialogOpen} onOpenChange={setIsAddTowerDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add New Tower</DialogTitle>
            <DialogDescription>Create a tower/block within a project</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddTower} className="space-y-4">
            <div className="space-y-2">
              <Label>Project</Label>
              <Select 
                value={towerForm.project_id} 
                onValueChange={(v) => setTowerForm({ ...towerForm, project_id: v })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tower Name</Label>
                <Input
                  value={towerForm.name}
                  onChange={(e) => setTowerForm({ ...towerForm, name: e.target.value })}
                  placeholder="Tower A"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Code</Label>
                <Input
                  value={towerForm.code}
                  onChange={(e) => setTowerForm({ ...towerForm, code: e.target.value.toUpperCase() })}
                  placeholder="TWR-A"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Total Floors</Label>
                <Input
                  type="number"
                  value={towerForm.total_floors}
                  onChange={(e) => setTowerForm({ ...towerForm, total_floors: e.target.value })}
                  placeholder="10"
                />
              </div>
              <div className="space-y-2">
                <Label>Units per Floor</Label>
                <Input
                  type="number"
                  value={towerForm.units_per_floor}
                  onChange={(e) => setTowerForm({ ...towerForm, units_per_floor: e.target.value })}
                  placeholder="4"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAddTowerDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create Tower
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Tower Dialog */}
      <Dialog open={isEditTowerDialogOpen} onOpenChange={setIsEditTowerDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Edit Tower</DialogTitle>
            <DialogDescription>Update tower details</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleEditTower} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tower Name</Label>
                <Input
                  value={towerForm.name}
                  onChange={(e) => setTowerForm({ ...towerForm, name: e.target.value })}
                  placeholder="Tower A"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Code</Label>
                <Input
                  value={towerForm.code}
                  onChange={(e) => setTowerForm({ ...towerForm, code: e.target.value.toUpperCase() })}
                  placeholder="TWR-A"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Total Floors</Label>
                <Input
                  type="number"
                  value={towerForm.total_floors}
                  onChange={(e) => setTowerForm({ ...towerForm, total_floors: e.target.value })}
                  placeholder="10"
                />
              </div>
              <div className="space-y-2">
                <Label>Units per Floor</Label>
                <Input
                  type="number"
                  value={towerForm.units_per_floor}
                  onChange={(e) => setTowerForm({ ...towerForm, units_per_floor: e.target.value })}
                  placeholder="4"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditTowerDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Update Tower
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Occupancy Dialog — Sub-component */}
      <OccupancyDialog
        open={isOccupancyDialogOpen}
        onOpenChange={setIsOccupancyDialogOpen}
        selectedUnit={selectedUnit}
        occupancyAction={occupancyAction}
        onSuccess={() => {
          setIsOccupancyDialogOpen(false);
          setIsUnitDialogOpen(false);
          loadUnits();
        }}
        submitting={submitting}
        setSubmitting={setSubmitting}
      />

      {/* CSV Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Bulk Upload Units
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to create multiple units at once
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Template download */}
            <div className="p-4 rounded-lg bg-muted/50 border border-dashed">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">CSV Template</p>
                  <p className="text-sm text-muted-foreground">Download the template to see the required format</p>
                </div>
                <Button variant="outline" size="sm" onClick={downloadCSVTemplate}>
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
            
            {/* File input */}
            <div className="space-y-2">
              <Label>Upload CSV File</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  className="flex-1"
                  data-testid="csv-file-input"
                />
              </div>
              {uploadFile && (
                <p className="text-sm text-muted-foreground">
                  Selected: {uploadFile.name}
                </p>
              )}
            </div>
            
            {/* Project selection */}
            <div className="space-y-2">
              <Label>Default Project (for rows without project_id)</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">None (use from CSV)</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Upload result */}
            {uploadResult && (
              <div className={`p-4 rounded-lg ${uploadResult.error_count > 0 ? 'bg-amber-50 dark:bg-amber-950/50' : 'bg-emerald-50 dark:bg-emerald-950/50'}`}>
                <div className="flex items-start gap-2">
                  {uploadResult.error_count > 0 ? (
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium">
                      {uploadResult.created_count} units created successfully
                    </p>
                    {uploadResult.error_count > 0 && (
                      <>
                        <p className="text-sm text-amber-600 mt-1">
                          {uploadResult.error_count} rows had errors:
                        </p>
                        <ul className="text-sm text-muted-foreground mt-1 max-h-32 overflow-y-auto">
                          {uploadResult.errors.slice(0, 10).map((err, i) => (
                            <li key={`upload-err-${i}`} className="truncate">• {err}</li>
                          ))}
                          {uploadResult.errors.length > 10 && (
                            <li>... and {uploadResult.errors.length - 10} more</li>
                          )}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsUploadDialogOpen(false);
              setUploadFile(null);
              setUploadResult(null);
            }}>
              {uploadResult ? 'Close' : 'Cancel'}
            </Button>
            {!uploadResult && (
              <Button 
                onClick={handleUploadCSV} 
                disabled={!uploadFile || submitting}
                data-testid="upload-csv-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedCount={selectedUnitIds.length}
        onClear={() => setSelectedUnitIds([])}
        onStatusAction={handleBulkStatusAction}
        onOccupancyAction={handleBulkOccupancyAction}
        userRole={user?.role}
        isReadOnly={isReadOnly}
      />

      {/* Bulk Status Dialog */}
      <BulkStatusDialog
        open={isBulkStatusDialogOpen}
        onOpenChange={setIsBulkStatusDialogOpen}
        unitIds={selectedUnitIds}
        statusAction={bulkStatusAction}
        onSuccess={handleBulkSuccess}
      />

      {/* Bulk Occupancy Dialog */}
      <BulkOccupancyDialog
        open={isBulkOccupancyDialogOpen}
        onOpenChange={setIsBulkOccupancyDialogOpen}
        unitIds={selectedUnitIds}
        onSuccess={handleBulkSuccess}
      />
    </div>
  );
};

export default InventoryPage;

